import {useCallback, useRef, useState} from 'react';

import {assert} from 'Asserts';
import runModal from 'ui-library/runModal';

import FlowchartConfirmExitWithUnsavedChangesModal, {
  FlowchartConfirmExitWithUnsavedChangesModalProps,
} from '../modals/FlowchartConfirmExitWithUnsavedChangesModal';

const ALL_TABS = 'ALL_TABS';

/**
 * Registered state for a sidepanel tab to track unsaved changes.
 */
export interface SidepanelTabState {
  /** This needs to be unique for each tab, recommend a descriptive name or UUID. */
  tabId: string;
  isDirty: boolean;
  onDiscard?: () => Promise<void> | void;
}

export type SidepanelRegisterTabState = Omit<SidepanelTabState, 'isDirty'>;

/**
 * Track the active tab for the main sidepanel tabs and the nested tab containers if present.
 */
export interface SidepanelActiveTabState {
  /** The active tab for the main sidepanel tabs */
  activeTabId: string;
  /** The active tab for the nested tab containers if present */
  nestedActiveTabId: string | null;
}

/**
 * Internal state of the sidepanel manager, determines which sidepanel is open, and the node and field that are selected.
 */
export interface SidepanelInternalState {
  nodeId: string | null;
  fieldId: string | null;
  openSidepanelType: SidepanelType | null;
  activeTabState: SidepanelActiveTabState | null;
}

const EMPTY_SIDEPANEL_INTERNAL_STATE: SidepanelInternalState = {
  nodeId: null,
  fieldId: null,
  openSidepanelType: null,
  activeTabState: null,
};

/**
 * Which sidepanel is currently open or being requested to open.
 */
export enum SidepanelType {
  /** Sidepanel responsible for node-level and field-level configuration
   *
   * In Benchling flowcharts, this should be on the right side of the screen.
   */
  NODE_SETTINGS = 'NODE_SETTINGS',
  /** Sidepanel responsible for flowchart-level configuration
   *
   * In Benchling flowcharts, this should be on the left side of the screen.
   */
  FLOWCHART_SETTINGS = 'FLOWCHART_SETTINGS',
}

/**
 * Request to change the sidepanel state by specifying the sidepanel and the node and field to select.
 */
export type SidepanelChange =
  | {
      sidepanel: SidepanelType;
      nodeId: string | null;
      fieldId?: string | null;
    }
  | {sidepanel: null; nodeId?: never; fieldId?: never};

interface UseFlowchartSidepanelManagerReturn {
  registerTab: (params: SidepanelRegisterTabState) => void;
  unregisterTab: (tab: string) => void;
  setIsTabDirty: (tab: string, isDirty: boolean) => void;
  changeTab: (newActiveTabState: SidepanelActiveTabState) => Promise<boolean>;
  changeSidepanel: ({
    sidepanelChange,
    onBeforeSidepanelChanged,
  }: {
    sidepanelChange: SidepanelChange;
    onBeforeSidepanelChanged?: () => Promise<void>;
  }) => Promise<boolean>;
  getEffectiveState: () => SidepanelInternalState;
  forceClose: () => void;
}

/**
 * Manages all sidepanel states in the flowchart config editor.
 *
 * Tracks state for both the right and left sidepanels. As each sidepanel opens,
 * its tabs can register to track unsaved changes. Prevents navigating away from
 * tabs when there are unsaved changes and uses a request-style pattern for
 * sidepanel changes. Ensures only one sidepanel can be open at a time.
 *
 * @param getNodeNameById - Function to get the node name by its ID (used for modal display).
 * @returns Object with methods to put into the sidepanel manager context.
 */
export default function useFlowchartSidepanelManager(
  getNodeNameById: (nodeId: string) => string | null
): UseFlowchartSidepanelManagerReturn {
  // Using refs to ensure changes to dirty state don't need a full render cycle before we can check against it
  // when changing the tabs or sidepanel state.
  const tabStates = useRef<Map<string, SidepanelTabState>>(new Map());
  const isModalOpenRef = useRef(false);

  const [sidepanelState, setSidepanelState] = useState<SidepanelInternalState>({
    ...EMPTY_SIDEPANEL_INTERNAL_STATE,
  });

  const getHasDirtyTabs = useCallback(
    () => Array.from(tabStates.current.values()).some((state) => state.isDirty),
    []
  );

  const getHasDirtyState = useCallback(
    (isNestedChange: boolean): boolean => {
      if (isNestedChange) {
        const nestedTabId = sidepanelState.activeTabState?.nestedActiveTabId;
        if (nestedTabId == null) {
          return false;
        }
        const tabState = tabStates.current.get(nestedTabId);
        return tabState?.isDirty ?? false;
      }
      return getHasDirtyTabs();
    },
    [sidepanelState.activeTabState, getHasDirtyTabs]
  );

  const clearAllTabStates = useCallback(() => {
    tabStates.current = new Map();
  }, []);

  const discardTabChanges = useCallback(async (tabId: string | null): Promise<void> => {
    if (tabId != null) {
      const tabState = tabStates.current.get(tabId);
      assert(tabState != null, 'Tab state must exist');
      await tabState.onDiscard?.();
      tabStates.current.delete(tabId);
    }
  }, []);

  const discardAllChanges = useCallback(async (): Promise<void> => {
    await Promise.all(Array.from(tabStates.current.values()).map((tabState) => tabState.onDiscard?.()));

    clearAllTabStates();
  }, [clearAllTabStates]);

  const registerTab = useCallback((params: SidepanelRegisterTabState) => {
    const {tabId, onDiscard} = params;
    const existingState = tabStates.current.get(tabId);

    if (existingState) {
      tabStates.current.set(tabId, {...existingState, onDiscard});
    } else {
      tabStates.current.set(tabId, {tabId, isDirty: false, onDiscard});
    }
  }, []);

  const unregisterTab = useCallback((tabId: string) => {
    tabStates.current.delete(tabId);
  }, []);

  const setIsTabDirty = useCallback((tabId: string, dirty: boolean) => {
    const currentState = tabStates.current.get(tabId);
    if (currentState && currentState.isDirty !== dirty) {
      tabStates.current.set(tabId, {...currentState, isDirty: dirty});
    }
  }, []);

  const markModalClosed = useCallback(() => {
    isModalOpenRef.current = false;
  }, []);

  const applyIncomingState = useCallback(
    (incomingState: SidepanelInternalState) => {
      setSidepanelState(incomingState);
      markModalClosed();
    },
    [markModalClosed]
  );

  const handleModalKeepEditing = useCallback(
    (resolve: (value: boolean) => void) => {
      markModalClosed();
      resolve(false);
    },
    [markModalClosed]
  );

  const handleModalLeaveWithoutSaving = useCallback(
    async (
      incomingState: SidepanelInternalState,
      resolve: (value: boolean) => void,
      tabIdToDiscard: string | 'ALL_TABS' | undefined
    ) => {
      if (tabIdToDiscard === ALL_TABS) {
        await discardAllChanges();
      } else if (tabIdToDiscard != null) {
        await discardTabChanges(tabIdToDiscard);
      } else {
        await discardAllChanges();
      }
      applyIncomingState(incomingState);
      resolve(true);
    },
    [discardAllChanges, discardTabChanges, applyIncomingState]
  );

  const showUnsavedChangesModal = useCallback(
    (incomingState: SidepanelInternalState, isNestedChange: boolean | null = null): Promise<boolean> => {
      if (isModalOpenRef.current) {
        return Promise.resolve(false);
      }

      assert(sidepanelState.nodeId != null, 'Sidepanel state must have a nodeId');
      const nodeName = getNodeNameById(sidepanelState.nodeId);
      assert(nodeName != null, 'Node name must exist');

      // Determine which tab to discard based on the change type
      // For nested changes, discard only the nested tab; for top-level or sidepanel changes, discard all
      const tabIdToDiscard =
        isNestedChange === true ? (sidepanelState.activeTabState?.nestedActiveTabId ?? ALL_TABS) : ALL_TABS;

      return new Promise<boolean>((resolve) => {
        isModalOpenRef.current = true;

        runModal<FlowchartConfirmExitWithUnsavedChangesModalProps>(
          FlowchartConfirmExitWithUnsavedChangesModal,
          {
            nodeName,
            // This is a quirk of runModal, keep editing will be closed on both the button and X clicks
            // But this field and wrapped later on to handle the actual close logic.
            onRequestClose: () => {},
            onRequestKeepEditing: () => {
              handleModalKeepEditing(resolve);
            },
            onRequestLeaveWithoutSaving: () => {
              handleModalLeaveWithoutSaving(incomingState, resolve, tabIdToDiscard);
            },
          }
        );
      });
    },
    [getNodeNameById, handleModalKeepEditing, handleModalLeaveWithoutSaving, sidepanelState]
  );

  const applyTabStateChange = useCallback(
    (newActiveTabState: SidepanelActiveTabState): void => {
      setSidepanelState({...sidepanelState, activeTabState: newActiveTabState});
    },
    [sidepanelState]
  );

  const checkAndHandleDirtyState = useCallback(
    async (newActiveTabState: SidepanelActiveTabState): Promise<boolean> => {
      const currentState = sidepanelState.activeTabState;
      const isNestedChange =
        currentState != null &&
        currentState.activeTabId === newActiveTabState.activeTabId &&
        currentState.nestedActiveTabId !== newActiveTabState.nestedActiveTabId;

      if (!getHasDirtyState(isNestedChange)) {
        return true;
      }

      const confirmed = await showUnsavedChangesModal(sidepanelState, isNestedChange);
      if (confirmed) {
        applyTabStateChange(newActiveTabState);
        return true;
      }
      return false;
    },
    [showUnsavedChangesModal, sidepanelState, getHasDirtyState, applyTabStateChange]
  );

  /**
   * Determines if within a sidepanel, the user can change to the requested tab state.
   * Updates the active tab state internally if the change is allowed.
   *
   * This should be called in any place where a tab change would be triggered.
   *
   * @param newActiveTabState - The desired active tab state.
   * @returns True if the tab can be changed or is already the same state, false otherwise. If false, caller
   * is responsible for ensuring the requested change is not applied.
   */
  const changeTab = useCallback(
    async (newActiveTabState: SidepanelActiveTabState): Promise<boolean> => {
      const currentState = sidepanelState.activeTabState;

      const areTabStatesEqual =
        currentState != null &&
        currentState.activeTabId === newActiveTabState.activeTabId &&
        currentState.nestedActiveTabId === newActiveTabState.nestedActiveTabId;

      // If no change needed, return early
      if (areTabStatesEqual) {
        return true;
      }

      const canChange = await checkAndHandleDirtyState(newActiveTabState);
      if (!canChange) {
        return false;
      }

      applyTabStateChange(newActiveTabState);
      return true;
    },
    [sidepanelState, checkAndHandleDirtyState, applyTabStateChange]
  );

  const forceClose = useCallback(() => {
    // There are situations that supersede discarding unsaved changes
    // (Ex: The selected node or field is being deleted)
    // Just reset the sidepanel state to the empty state
    clearAllTabStates();
    setSidepanelState(EMPTY_SIDEPANEL_INTERNAL_STATE);
  }, [clearAllTabStates]);

  const buildStateToApply = useCallback(
    (sidepanelChange: SidepanelChange): SidepanelInternalState => ({
      nodeId: sidepanelChange.nodeId ?? null,
      fieldId: sidepanelChange.fieldId ?? null,
      openSidepanelType: sidepanelChange.sidepanel,
      activeTabState: null,
    }),
    []
  );

  const areSidepanelStatesEqual = useCallback(
    (state1: SidepanelInternalState, state2: SidepanelInternalState): boolean => {
      const isSameNodeId = state1.nodeId === state2.nodeId;
      const isSameFieldId = state1.fieldId === state2.fieldId;
      const isSameOpenSidepanelType = state1.openSidepanelType === state2.openSidepanelType;

      return isSameNodeId && isSameFieldId && isSameOpenSidepanelType;
    },
    []
  );

  const applySidepanelChange = useCallback(
    async ({
      sidepanelChange,
      onBeforeSidepanelChanged,
    }: {
      sidepanelChange: SidepanelChange;
      onBeforeSidepanelChanged?: () => Promise<void>;
    }): Promise<boolean> => {
      let stateToApply = buildStateToApply(sidepanelChange);
      const stayingOnSameNode = sidepanelChange.nodeId === sidepanelState.nodeId;
      const stayingOnSameSidepanel = sidepanelChange.sidepanel === sidepanelState.openSidepanelType;
      const hasDirtyTabs = getHasDirtyTabs();

      if (hasDirtyTabs && (!stayingOnSameNode || !stayingOnSameSidepanel)) {
        const confirmed = await showUnsavedChangesModal(stateToApply);
        if (!confirmed) {
          return false;
        }
      }

      if (onBeforeSidepanelChanged != null) {
        await onBeforeSidepanelChanged();
        stateToApply = buildStateToApply(sidepanelChange);
      }

      // Reset active tabs when switching nodes or sidepanels
      const isLeavingSidepanel = !(stayingOnSameNode && stayingOnSameSidepanel);
      setSidepanelState({
        ...stateToApply,
        activeTabState: isLeavingSidepanel ? null : sidepanelState.activeTabState,
      });
      return true;
    },
    [getHasDirtyTabs, showUnsavedChangesModal, sidepanelState, buildStateToApply]
  );

  /**
   * Attempts to change the sidepanel to the requested state, and returns true if the change was applied.
   *
   * This should be called in any place where a sidepanel change would be triggered, and the caller should handle any necessary side effects.
   *
   * @param params.sidepanelChange - The sidepanel change request.
   * @param params.onBeforeSidepanelChanged - Optional callback that returns a Promise<void>. If provided, this will be
   * executed after confirming that no modal was needed or the user opted to discard changes. This is useful for
   * applying changes (e.g., adding a new node) and waiting for them to propagate before switching the sidepanel.
   * @returns True if the sidepanel was changed or is already the same state, false otherwise. If false,
   * caller is responsible for ensuring the requested change is not applied.
   */
  const changeSidepanel = useCallback(
    async ({
      sidepanelChange,
      onBeforeSidepanelChanged,
    }: {
      sidepanelChange: SidepanelChange;
      onBeforeSidepanelChanged?: () => Promise<void>;
    }): Promise<boolean> => {
      const stateToApply = buildStateToApply(sidepanelChange);

      if (areSidepanelStatesEqual(stateToApply, sidepanelState)) {
        // Equal sidepanel states is equivalent to a successful sidepanel change.
        // We still want to do the desired prework in this case.
        if (onBeforeSidepanelChanged != null) {
          await onBeforeSidepanelChanged();
        }
        return true;
      }

      return await applySidepanelChange({sidepanelChange, onBeforeSidepanelChanged});
    },
    [sidepanelState, buildStateToApply, areSidepanelStatesEqual, applySidepanelChange]
  );

  const getEffectiveState = useCallback((): SidepanelInternalState => sidepanelState, [sidepanelState]);

  return {
    registerTab,
    unregisterTab,
    setIsTabDirty,
    changeTab,
    forceClose,
    getEffectiveState,
    changeSidepanel,
  };
}
